import { FunctionComponent, useCallback, useMemo } from 'react'

import { useLinkTo } from '@react-navigation/native'
import { OpportunityDTO } from 'integration/resources/wallet'
import { Button, Box, Center, HStack, Skeleton, Text, useTheme, VStack } from 'native-base'
import { Platform } from 'react-native'
import { changeToCurrency } from 'src/utils/currencyFormat'

type OpportunitySummaryProps = {
  opportunity?: OpportunityDTO | null
  isLoading?: boolean
}

const parseHexColor = (hexColor?: string) => {
  if (!hexColor) return null

  const normalized = hexColor.trim()

  if (normalized.startsWith('#')) {
    const value = normalized.replace('#', '')

    if (value.length === 3) {
      const [r, g, b] = value.split('')

      return {
        r: parseInt(`${r}${r}`, 16),
        g: parseInt(`${g}${g}`, 16),
        b: parseInt(`${b}${b}`, 16)
      }
    }

    if (value.length === 6) {
      return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16)
      }
    }
  }

  const rgbMatch = normalized.match(/^rgb\((\d{1,3}),\s?(\d{1,3}),\s?(\d{1,3})\)$/i)

  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3])
    }
  }

  return null
}

const resolveTagTextColor = (backgroundColor: string | null | undefined, fallback: string) => {
  const parsedColor = parseHexColor(backgroundColor ?? undefined)

  if (!parsedColor) return fallback

  const luminance = (0.299 * parsedColor.r + 0.587 * parsedColor.g + 0.114 * parsedColor.b) / 255

  return luminance > 0.6 ? 'gray.700' : 'white'
}

export const OpportunitySummary: FunctionComponent<OpportunitySummaryProps> = ({
  opportunity,
  isLoading
}) => {
  const linkTo = useLinkTo()

  const theme = useTheme()

  const handleOpenOpportunity = useCallback(() => {
    if (!opportunity?.id) return

    const path = `/oportunidade/${opportunity.id}/${opportunity.type === 'odc' ? 1 : 2}`

    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        window.open(path, '_blank')
      }

      return
    }

    linkTo(path)
  }, [linkTo, opportunity])

  const metrics = useMemo(() => {
    if (!opportunity) return []

    const leadsCount =
      opportunity.leads !== null && opportunity.leads !== undefined
        ? Number(opportunity.leads)
        : null

    const chargesCount =
      opportunity.charges !== null && opportunity.charges !== undefined
        ? Number(opportunity.charges)
        : null

    const hasOpportunityData =
      (leadsCount !== null && !Number.isNaN(leadsCount) && leadsCount > 0) ||
      (chargesCount !== null && !Number.isNaN(chargesCount) && chargesCount > 0)

    if (!hasOpportunityData) return []

    const summary: string[] = []

    if (leadsCount !== null && !Number.isNaN(leadsCount)) {
      summary.push(`${leadsCount} ${leadsCount === 1 ? 'Lead' : 'Leads'}`)
    }

    if (chargesCount !== null && !Number.isNaN(chargesCount)) {
      summary.push(`${chargesCount} ${chargesCount === 1 ? 'Cobrança' : 'Cobranças'}`)
    }

    return summary
  }, [opportunity])

  if (isLoading) {
    return <Skeleton borderRadius="20px" h={{ base: '96px', lg: '88px' }} w="full" />
  }

  if (!opportunity) return null

  const numericValue = Number(opportunity.value)

  const hasValidValue =
    opportunity.value !== null && opportunity.value !== undefined && Number.isFinite(numericValue)

  const formattedValue = hasValidValue ? changeToCurrency(numericValue) : '--'

  const metricsDescription = metrics.join(' • ')

  const tagBackground = opportunity.tag_color ?? theme.colors.gray[100]

  const tagTextColor = resolveTagTextColor(opportunity.tag_color, 'gray.500')

  return (
    <Box bg="white" borderRadius="20px" p={6}>
      <HStack
        flexDirection={{ base: 'column', lg: 'row' }}
        alignItems={{ base: 'flex-start', lg: 'center' }}
        justifyContent="space-between"
        space={6}>
        <VStack space={3} flex={1} w="full">
          <HStack space={3} alignItems="center" flexWrap="wrap">
            <Text color="gray.700" fontSize="18px" fontWeight="bold">
              Oportunidade:
            </Text>

            <Text color="secondary.500" fontSize="18px" fontWeight="bold">
              {formattedValue}
            </Text>

            {(!!opportunity.tag || opportunity.type) && (
              <Center
                px={3}
                h={6}
                minW={12}
                borderRadius="8px"
                bgColor={opportunity.tag ? tagBackground : theme.colors.gray[100]}
                borderWidth={0}
                justifyContent="center"
                alignItems="center">
                <Text color={tagTextColor} fontSize="12px" fontWeight="semibold">
                  {opportunity.tag ?? opportunity.type.toUpperCase()}
                </Text>
              </Center>
            )}
          </HStack>

          <Text color="gray.400" fontSize="14px" fontWeight="semibold">
            {metricsDescription
              ? `Esta TAB possui `
              : 'Confira as recomendações dessa oportunidade!'}
            {metricsDescription && (
              <Text color="secondary.500" fontSize="14px" fontWeight="semibold">
                {metricsDescription}
              </Text>
            )}
          </Text>
        </VStack>

        <Button
          variant="outline"
          colorScheme="gray"
          px={6}
          h="50px"
          _pressed={{ opacity: 0.7 }}
          _text={{ fontSize: '14px', fontWeight: 700, lineHeight: '20px' }}
          isDisabled={isLoading}
          _disabled={{ opacity: 0.7 }}
          onPress={handleOpenOpportunity}>
          Ver oportunidade
        </Button>
      </HStack>
    </Box>
  )
}
